import { useLayoutEffect, useState } from 'react';

/**
 * Sizes a note's writing box to its text (the *Quiet composer* layout of the UI revamp preview): at least `minRows`
 * lines, growing a line at a time as the text does, up to `maxRows` lines, after which the text scrolls inside the
 * box. The drag handle is removed, since the box sizes itself — and a box dragged without limit could push the notes
 * past the bottom of a card that scrolls inside itself. It measures when the box appears (the composer and the edit
 * box open after the section), when the text changes, and when the box's width does (a narrower box wraps to more
 * lines).
 *
 * Done here rather than with CSS `field-sizing: content`, which not every supported browser has; the line height,
 * padding and borders are read from the drawn box, so it follows the theme. Returns a callback ref for the
 * `<textarea>`.
 */
export function useAutoGrowTextarea(
    value: string,
    minRows = 4,
    maxRows = 10,
): (box: HTMLTextAreaElement | null) => void {
    const [box, setBox] = useState<HTMLTextAreaElement | null>(null);
    // biome-ignore lint/correctness/useExhaustiveDependencies: `value` is read through the drawn box; a new value is re-measured.
    useLayoutEffect(() => {
        if (box === null) {
            return undefined;
        }
        const fit = () => {
            const style = getComputedStyle(box);
            const fontSize = Number.parseFloat(style.fontSize) || 16;
            const line = Number.parseFloat(style.lineHeight) || fontSize * 1.5;
            const padding = Number.parseFloat(style.paddingTop) + Number.parseFloat(style.paddingBottom);
            const border =
                Number.parseFloat(style.borderTopWidth) + Number.parseFloat(style.borderBottomWidth);
            const least = line * minRows + padding + border;
            const most = line * maxRows + padding + border;
            box.style.resize = 'none';
            box.style.height = 'auto';
            const wanted = box.scrollHeight + border;
            box.style.height = `${Math.min(Math.max(wanted, least), most)}px`;
            box.style.overflowY = wanted > most ? 'auto' : 'hidden';
        };
        fit();
        if (typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        let width = box.clientWidth;
        const observer = new ResizeObserver(() => {
            if (box.clientWidth !== width) {
                width = box.clientWidth;
                fit();
            }
        });
        observer.observe(box);
        return () => observer.disconnect();
    }, [box, value, minRows, maxRows]);
    return setBox;
}
